// Mock data for Solid Consulting website

export const companyInfo = {
  name: "Solid Consulting",
  tagline: "Solid engineering. Real impact.",
  description: "Senior IT consulting partner delivering reliable, delivery-focused tech solutions.",
  email: "contact@solidconsulting.fr",
  phone: "+33 6 35 63 97 43",
  address: "44 av de lattre de tassigny, Marcq-en-Barœul 59700, France"
};

export const navigation = [
  { name: "Services", href: "/services" },
  { name: "Case Studies", href: "/case-studies" },
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" }
];

export const services = [
  {
    id: "web-development",
    title: "Web Development",
    shortDescription: "Modern SPAs, portals, dashboards, and B2B/B2C applications built with cutting-edge frameworks.",
    description: "We build performant, scalable web applications that drive business results. From complex dashboards to customer-facing platforms, our team delivers pixel-perfect implementations with robust architecture.",
    technologies: ["React", "Angular", "Vue.js", "Next.js", "TypeScript"],
    missions: [
      "Single Page Applications (SPA) development",
      "Enterprise portal creation",
      "Real-time dashboard implementations",
      "B2B/B2C e-commerce platforms",
      "Progressive Web Apps (PWA)"
    ],
    outcomes: [
      "50% faster load times",
      "Improved user engagement",
      "Reduced bounce rates",
      "Seamless cross-device experience"
    ]
  },
  {
    id: "mobile-development",
    title: "Mobile Development",
    shortDescription: "Native and cross-platform mobile apps for iOS and Android with exceptional user experience.",
    description: "We create mobile applications that users love. Whether native or cross-platform, our apps are built with performance, security, and scalability in mind.",
    technologies: ["React Native", "Flutter", "Swift", "Kotlin", "iOS", "Android"],
    missions: [
      "Native iOS and Android development",
      "Cross-platform app development",
      "Mobile app modernization",
      "App Store optimization",
      "Mobile backend integration"
    ],
    outcomes: [
      "4.5+ App Store ratings",
      "Reduced development costs",
      "Faster time-to-market",
      "Consistent cross-platform UX"
    ]
  },
  {
    id: "backend-apis",
    title: "Backend & APIs",
    shortDescription: "Robust microservices architecture and API-first development with Java, Spring Boot, Node.js.",
    description: "We design and implement scalable backend systems that power modern applications. Our API-first approach ensures flexibility, maintainability, and seamless integrations.",
    technologies: ["Java", "Spring Boot", "Node.js", "NestJS", "REST", "GraphQL"],
    missions: [
      "Microservices architecture design",
      "REST & GraphQL API development",
      "Legacy system modernization",
      "API gateway implementation",
      "Event-driven architecture"
    ],
    outcomes: [
      "99.9% API uptime",
      "Sub-100ms response times",
      "Simplified integrations",
      "Reduced technical debt"
    ]
  },
  {
    id: "cloud-devops",
    title: "Cloud & DevOps",
    shortDescription: "AWS and Azure expertise with CI/CD, serverless, containers, and Kubernetes.",
    description: "We help organizations embrace the cloud with confidence. From migration strategies to fully automated pipelines, we ensure your infrastructure is resilient, secure, and cost-effective.",
    technologies: ["AWS", "Azure", "Kubernetes", "Docker", "Terraform", "CI/CD"],
    missions: [
      "Cloud migration & strategy",
      "CI/CD pipeline implementation",
      "Container orchestration (Kubernetes)",
      "Serverless architecture",
      "Infrastructure as Code (IaC)"
    ],
    outcomes: [
      "60% reduction in deployment time",
      "Improved system reliability",
      "Optimized cloud costs",
      "Zero-downtime deployments"
    ]
  },
  {
    id: "consulting-architecture",
    title: "Consulting & Architecture",
    shortDescription: "Strategic guidance on API-first design, cloud architecture, technical audits, and team coaching.",
    description: "We provide expert guidance to help you make the right technical decisions. From architecture reviews to team coaching, we help you build sustainable engineering practices.",
    technologies: ["Architecture Design", "Technical Audits", "Team Coaching", "Best Practices"],
    missions: [
      "Technical architecture design",
      "Code & architecture audits",
      "API-first strategy consulting",
      "Team upskilling & coaching",
      "Technology roadmap planning"
    ],
    outcomes: [
      "Clear technical roadmap",
      "Reduced technical risk",
      "Improved team velocity",
      "Sustainable architecture"
    ]
  }
];

export const caseStudies = [
  {
    id: "fintech-platform",
    title: "FinTech Trading Platform",
    client: "Leading European Bank",
    industry: "Finance",
    context: "A major European bank needed to modernize their legacy trading platform to handle increasing transaction volumes and regulatory requirements.",
    challenge: "The existing monolithic system couldn't scale to meet peak trading demands, causing latency issues and customer complaints. Compliance with new financial regulations required significant architectural changes.",
    solution: "We designed and implemented a microservices-based architecture using Spring Boot and Kubernetes. Real-time data processing was handled through Apache Kafka, with a React-based trading dashboard for users.",
    techStack: ["Java", "Spring Boot", "Kubernetes", "Apache Kafka", "React", "PostgreSQL", "AWS"],
    results: [
      { metric: "Transaction Throughput", value: "10x increase" },
      { metric: "Latency Reduction", value: "85%" },
      { metric: "System Uptime", value: "99.99%" },
      { metric: "Compliance", value: "100% regulatory adherence" }
    ]
  },
  {
    id: "retail-ecommerce",
    title: "Omnichannel Retail Platform",
    client: "Global Retail Chain",
    industry: "Retail",
    context: "A global retail chain with 500+ stores needed a unified platform to connect online and in-store experiences for their customers.",
    challenge: "Disconnected systems led to inventory discrepancies, inconsistent pricing, and a fragmented customer experience across channels.",
    solution: "We built an API-first platform with real-time inventory sync, unified customer profiles, and a responsive web app. Mobile apps for store associates enabled seamless cross-channel transactions.",
    techStack: ["Node.js", "NestJS", "React Native", "GraphQL", "MongoDB", "Azure", "Redis"],
    results: [
      { metric: "Online Revenue", value: "+45%" },
      { metric: "Inventory Accuracy", value: "99.5%" },
      { metric: "Customer Satisfaction", value: "+35 NPS" },
      { metric: "Order Fulfillment", value: "2x faster" }
    ]
  },
  {
    id: "industrial-iot",
    title: "Industrial IoT Platform",
    client: "Manufacturing Corporation",
    industry: "Industry",
    context: "A manufacturing company with 20 factories worldwide needed real-time visibility into equipment performance and predictive maintenance capabilities.",
    challenge: "Legacy monitoring systems provided delayed data, leading to unexpected equipment failures and costly downtime. Data silos prevented effective analysis across facilities.",
    solution: "We developed a cloud-native IoT platform on AWS with edge computing for real-time data processing. Machine learning models predicted equipment failures before they occurred.",
    techStack: ["Python", "AWS IoT", "Lambda", "React", "TensorFlow", "TimescaleDB", "Grafana"],
    results: [
      { metric: "Downtime Reduction", value: "70%" },
      { metric: "Maintenance Costs", value: "-40%" },
      { metric: "Data Latency", value: "< 1 second" },
      { metric: "Equipment Efficiency", value: "+25%" }
    ]
  },
  {
    id: "saas-platform",
    title: "SaaS Platform Scaling",
    client: "B2B SaaS Startup",
    industry: "SaaS",
    context: "A fast-growing B2B SaaS startup needed to scale their platform from 100 to 10,000+ enterprise customers without compromising performance.",
    challenge: "The initial architecture couldn't handle multi-tenancy at scale. Database queries were becoming bottlenecks, and deployment cycles were slowing down feature delivery.",
    solution: "We re-architected the platform with proper multi-tenancy patterns, implemented database sharding, and built a robust CI/CD pipeline with feature flags for controlled rollouts.",
    techStack: ["Node.js", "React", "PostgreSQL", "Kubernetes", "GitHub Actions", "Datadog"],
    results: [
      { metric: "Customer Capacity", value: "100x increase" },
      { metric: "Query Performance", value: "10x faster" },
      { metric: "Deploy Frequency", value: "Daily vs. Monthly" },
      { metric: "Incident Response", value: "< 5 minutes" }
    ]
  }
];

export const benefits = [
  {
    title: "Senior Expertise",
    description: "Our consultants average 10+ years of experience. No juniors learning on your project."
  },
  {
    title: "Delivery Focus",
    description: "We ship working software. Clear milestones, transparent progress, and measurable outcomes."
  },
  {
    title: "API-First Mindset",
    description: "Every system we build is designed for integration, scalability, and future-proofing."
  },
  {
    title: "Cloud Native",
    description: "We leverage cloud platforms to build resilient, cost-effective, and globally scalable solutions."
  }
];

export const workProcess = [
  {
    step: 1,
    title: "Discover",
    description: "We dive deep into your business context, technical landscape, and goals to understand the full picture."
  },
  {
    step: 2,
    title: "Design",
    description: "We architect solutions that balance innovation with pragmatism, ensuring feasibility and scalability."
  },
  {
    step: 3,
    title: "Build",
    description: "We develop iteratively with continuous feedback, delivering working software in short cycles."
  },
  {
    step: 4,
    title: "Run",
    description: "We ensure smooth handover, provide documentation, and support your team for long-term success."
  }
];

export const industries = [
  { name: "Finance", icon: "building" },
  { name: "Retail", icon: "shopping-cart" },
  { name: "Industry", icon: "factory" },
  { name: "SaaS", icon: "cloud" },
  { name: "Healthcare", icon: "heart-pulse" },
  { name: "Logistics", icon: "truck" }
];

export const aboutValues = [
  {
    title: "Quality",
    description: "We don't cut corners. Every line of code, every architecture decision is made with long-term maintainability in mind."
  },
  {
    title: "Transparency",
    description: "No surprises. Clear communication, honest assessments, and regular progress updates are our standard."
  },
  {
    title: "Ownership",
    description: "We treat your project as our own. We're not just executing tasks—we're invested in your success."
  },
  {
    title: "Continuous Learning",
    description: "Technology evolves fast. We stay current through constant learning, experimentation, and knowledge sharing."
  }
];

export const aboutStory = {
  title: "Our Story",
  content: `Solid Consulting was founded in 2024 by a team of senior engineers who were frustrated with the IT consulting status quo. Too often, we saw projects staffed with inexperienced consultants, leading to delays, budget overruns, and subpar results.

We believed there was a better way. A consulting company that only employed senior talent. A partner that focused on delivering real value, not just billing hours. A team that took ownership of outcomes, not just outputs.

Today, Solid Consulting has grown to over 10 senior consultants serving clients across Europe and Africa. Our track record speaks for itself: 95% client retention rate, a dozen successful project deliveries, and a reputation for technical excellence.

We're not the biggest consulting firm—and we don't want to be. We're the firm you call when you need it done right.`
};

export const blogPosts = [
  {
    id: "api-first-design",
    title: "API-First Design: Why It Matters for Modern Applications",
    summary: "Discover how adopting an API-first approach can accelerate development, improve collaboration, and future-proof your software architecture.",
    category: "Architecture",
    readTime: "8 min",
    date: "2025-07-15"
  },
  {
    id: "microservices-pitfalls",
    title: "5 Common Microservices Pitfalls and How to Avoid Them",
    summary: "Learn from real project experiences: the mistakes teams make when adopting microservices and practical strategies to prevent them.",
    category: "DevOps",
    readTime: "12 min",
    date: "2025-06-28"
  },
  {
    id: "cloud-cost-optimization",
    title: "Cloud Cost Optimization: A Practical Guide for CTOs",
    summary: "Concrete strategies to reduce your AWS or Azure bill by 30-50% without sacrificing performance or reliability.",
    category: "Cloud",
    readTime: "10 min",
    date: "2025-06-10"
  },
  {
    id: "spring-boot-best-practices",
    title: "Spring Boot in Production: Best Practices for Enterprise Applications",
    summary: "Battle-tested patterns for building robust, maintainable Spring Boot applications at scale.",
    category: "Backend",
    readTime: "15 min",
    date: "2025-05-22"
  },
  {
    id: "kubernetes-migration",
    title: "Kubernetes Migration: A Step-by-Step Approach",
    summary: "How to migrate your applications to Kubernetes without disrupting your business operations.",
    category: "DevOps",
    readTime: "14 min",
    date: "2025-05-05"
  }
];

export const faqs = [
  {
    question: "What types of companies do you work with?",
    answer: "We work with scale-ups, mid-sized companies, and large enterprises across various industries including finance, retail, healthcare, and SaaS. Our sweet spot is organizations with technical teams who need senior expertise to accelerate delivery or tackle complex challenges."
  },
  {
    question: "How do you ensure consultant quality?",
    answer: "Every consultant at Solid Consulting has at least 8 years of professional experience. We have a rigorous vetting process that includes technical assessments, real-world problem-solving exercises, and cultural fit evaluation. We don't hire juniors—period."
  },
  {
    question: "What engagement models do you offer?",
    answer: "We offer flexible engagement models including time & materials for ongoing collaboration, fixed-price for well-defined projects, and dedicated team setups for long-term partnerships. We'll recommend the best model based on your specific needs."
  },
  {
    question: "Can you work with our existing team?",
    answer: "Absolutely. We're experienced at integrating with client teams, whether it's augmenting your existing developers, leading a specific workstream, or providing architectural guidance. We adapt to your processes and tools."
  },
  {
    question: "What's your approach to project management?",
    answer: "We're agile by default but pragmatic in practice. We work in short iterations with regular demos and feedback loops. Transparency is key—you'll always know what we're working on, what's delivered, and what's coming next."
  }
];

export const contactFormFields = [
  { name: "name", label: "Full Name", type: "text", required: true },
  { name: "company", label: "Company", type: "text", required: true },
  { name: "email", label: "Email Address", type: "email", required: true },
  {
    name: "projectType",
    label: "Project Type",
    type: "select",
    required: true,
    options: [
      "Web Development",
      "Mobile Development",
      "Backend & APIs",
      "Cloud & DevOps",
      "Consulting & Architecture",
      "Other"
    ]
  },
  {
    name: "budget",
    label: "Budget Range",
    type: "select",
    required: false,
    options: [
      "< $50,000",
      "$50,000 - $150,000",
      "$150,000 - $500,000",
      "> $500,000",
      "Not sure yet"
    ]
  },
  { name: "message", label: "Tell us about your project", type: "textarea", required: true }
];
